import React, { FC, Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { useHistory, useLocation } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import ListImageName from "go-app/components/ImageRenderer/ListImageName";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import useSubscriptionBasedRestriction from "go-core/components/SubscriptionBasedRestriction/hooks/useSubscriptionBasedRestriction";
import { useWindowSize } from "go-core/components/useWindowSize";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListFilterSource } from "go-list/core/components/Filter/services/types";
import { ListConfigField } from "go-list/core/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { EmployeeApi } from "../../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../../utils/entityStatus/entityStatus";
import { handleAddEmployeeBasedOnSubscription } from "../../../utils";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "EMPLOYEE";
const resourceType = "EMPLOYEE";

const List: FC<ListState> = ({ resource, mobileActions }) => {
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addSuccessFlash } = useFlash();
	const segmentContextValue = React.useContext(SegmentContext);
	const {
		handleShowSubscriptionBasedRestrictionModal,
		renderSubscriptionBasedRestrictionModal,
		handleSetSubscriptionBasedRestrictionModalConfig,
	} = useSubscriptionBasedRestriction();

	if (!resource) return null;
	const data = resource.read();

	const onRemoveEmployee = async (item: EmployeeApi) => {
		await apiOrganization.removeEmployee(item.id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onHireEmployee = async (item: EmployeeApi) => {
		await apiOrganization.hireEmployee(item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onFireEmployee = async (item: EmployeeApi) => {
		await apiOrganization.fireEmployee(item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onFireEmployees = async (
		multipleActionItems: EmployeeApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.fireEmployees(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onHireEmployees = async (
		multipleActionItems: EmployeeApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.hireEmployees(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onRemoveEmployees = async (
		multipleActionItems: EmployeeApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.removeEmployees(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onCopyEmployee = async (employeeId: number) => {
		const callback = () => history.push({ pathname: `/${organization.id}/employees/new`, state: { employeeId } });
		await handleAddEmployeeBasedOnSubscription(
			organization,
			handleSetSubscriptionBasedRestrictionModalConfig,
			t,
			handleShowSubscriptionBasedRestrictionModal,
			callback
		);
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				render: (item: EmployeeApi) => {
					return (
						<StickyColumnWithEntityStatus status={convertListStatusToEntityStatus(item.status)}>
							<ListImageName
								showColor={true}
								data={{ ...item, limitTextWidth: 200 }}
								imageLink={item.avatar_link}
								link={`${location.pathname}/${item.id}`}
							/>
						</StickyColumnWithEntityStatus>
					);
				},
				renderExport: (item: EmployeeApi) => item.name,
			},
			{
				id: "role",
				name: t("modules.employee.field.role.title"),
				type: "search_select",
				render: (item: EmployeeApi) => {
					return <span>{item.venue_role?.name}</span>;
				},
				renderExport: (item: EmployeeApi) => {
					return item.venue_role?.name;
				},
				source: {
					request: (search: string, filterParams: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getRolesSearchSelect(search, filterParams, {
							cancelToken: options?.token,
						}),
				} as ListFilterSource,
			},
			{
				id: "card_code",
				name: t("modules.employee.field.card_code.title"),
				type: "text",
			},
			{
				id: "hourly_rate",
				name: t("modules.employee.field.hourly_rate_net.title"),
				type: "number",
				render: (item: EmployeeApi) => {
					return FormatMoney(item.hourly_rate);
				},
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "workplace",
				name: t("modules.employee.field.workplace.title"),
				type: "search_select",
				render: (item: EmployeeApi) => {
					return item.workplace?.name;
				},
				source: {
					request: (search: string, filterParams: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getWorkplacesSearchSelect(search, filterParams, {
							cancelToken: options?.token,
						}),
				} as ListFilterSource,
			} as ListConfigField,
		],
		filters: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				type: "text",
				typeOptions: ["c", "e"],
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				options: {
					HIRED: t("enums.common.status.HIRED"),
					FIRED: t("enums.common.status.FIRED"),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
				hasDefaultOptions: false,
			},
			{
				id: "contact_email",
				name: t("common.word.email", { ns: "lib" }),
				type: "text",
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: EmployeeApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: EmployeeApi) => `${location.pathname}/${item.id}/edit`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				visible: (item: EmployeeApi) => item.status !== "REMOVED",
				click: (item: EmployeeApi) => onRemoveEmployee(item),
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.irreversible_action", { ns: "lib" }),
				},
			},
			{
				name: t("modules.employee.action.hire.title"),
				visible: (item: EmployeeApi) => {
					return item.status === "FIRED";
				},
				click: (item: EmployeeApi) => onHireEmployee(item),
				refreshListAfter: true,
				confirmation: { use: true },
			},
			{
				name: t("modules.employee.action.fire.title"),
				visible: (item: EmployeeApi) => {
					return item.status === "HIRED";
				},
				click: (item: EmployeeApi) => onFireEmployee(item),
				refreshListAfter: true,
				confirmation: { use: true },
			},
			{
				name: t("common.action.copy", { ns: "lib" }),
				click: (item: EmployeeApi) => onCopyEmployee(item.id),
			},
		],
		multipleActions: [
			{
				name: t("modules.employee.action.fire.title"),
				click: (
					multipleActionsItems: EmployeeApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onFireEmployees(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: EmployeeApi[]) =>
					multipleActionsItems.some((item) => item.status === "HIRED"),
				refreshListAfter: true,
				confirmation: { use: true },
			},
			{
				name: t("modules.employee.action.hire.title"),
				click: (
					multipleActionsItems: EmployeeApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onHireEmployees(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: EmployeeApi[]) =>
					multipleActionsItems.some((item) => item.status === "FIRED"),
				refreshListAfter: true,
				confirmation: { use: true },
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (
					multipleActionsItems: EmployeeApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onRemoveEmployees(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: EmployeeApi[]) =>
					multipleActionsItems.some((item) => item.status !== "DELETED"),
				refreshListAfter: true,
				confirmation: { use: true, message: t("confirmation.message.irreversible_action", { ns: "lib" }) },
			},
		],
		selectedColumns: ["name", "role", "workplace", "hourly_rate"],
		segments: [
			{
				id: "all",
				name: t("common.word.all_male"),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted_male", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e",
					},
				],
			},
			{
				id: "hired",
				name: t("modules.employee.field.hired.title"),
				slug: "hired",
				filters: [
					{
						filterId: "status",
						value: "HIRED",
						condition: "e",
					},
				],
			},
			{
				id: "fired",
				name: t("modules.employee.field.fired.title"),
				slug: "fired",
				filters: [
					{
						filterId: "status",
						value: "FIRED",
						condition: "e",
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "hired"),
		exportConfig: {
			title: t("modules.employee.field.export_config.title"),
			filename: t("modules.employee.header.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		fetch: async (fetchParams: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			fetchParams.include = "role,workplace,custom_fields";
			return apiOrganization.getEmployees(fetchParams, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		getStatus: (item: EmployeeApi) => {
			if (item.status === "HIRED") return "ENABLED";
			if (item.status === "FIRED") return "WARNING";
			return "DISABLED";
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
		fetchTotalItemsCountForSelectedFilters: (
			fetchTotalItemsParams: Record<string, any>,
			sourceToken?: CancelTokenSource
		) => apiOrganization.getEmployeeCount(fetchTotalItemsParams, { cancelToken: sourceToken?.token }),
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<>
			<ListData
				config={config}
				emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
				canManageMultiActionsForAllItems
				mobileActions={mobileActions}
			/>
			{renderSubscriptionBasedRestrictionModal()}
		</>
	);
};

const OrganizationEmployeesIndexIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "EMPLOYEE",
			name: t(`enums.custom_fields.resources.EMPLOYEE`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const {
		handleShowSubscriptionBasedRestrictionModal,
		renderSubscriptionBasedRestrictionModal,
		handleSetSubscriptionBasedRestrictionModalConfig,
	} = useSubscriptionBasedRestriction();
	const organization = useSelector(selectOrganization);
	const history = useHistory();

	useEffect(() => {
		handleChangeTabTitle(t("modules.employee.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	const handleAddEmployee = async () => {
		const callback = () => history.push(`${props.match.url}/new`);
		await handleAddEmployeeBasedOnSubscription(
			organization,
			handleSetSubscriptionBasedRestrictionModalConfig,
			t,
			handleShowSubscriptionBasedRestrictionModal,
			callback
		);
	};

	const buttons: ButtonProps[] = [
		{
			action: handleAddEmployee,
			title: t("common.action.add", { ns: "lib" }),
			variant: "primary",
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			action: handleAddEmployee,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.employee.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
			{renderSubscriptionBasedRestrictionModal()}
		</>
	);
};
export default OrganizationEmployeesIndexIndexPage;
